import React from "react";
import styled from "styled-components";
import { Icon } from "../Icon";
import { useEvent } from "../hooks/useEvent";
import { style } from "../style";
import { ItemizedGrandTotal } from "./ItemizedGrandTotal";
import { ItemizedList } from "./ItemizedList";
import { ItemizedSpacer } from "./ItemizedSpacer";
import { ItemizedSummaryLineItem, ItemizedSummaryTotals } from "./types";

const Container = styled.div`
  all: unset;
  box-sizing: border-box;
  width: 100%;

  display: flex;
  flex-direction: column;
  align-items: center;

  overflow-y: scroll;
  scrollbar-width: none;
`;

const GrandTotalWrapper = styled.div`
  all: unset;
  box-sizing: border-box;
  width: 100%;

  display: flex;
  align-items: center;
  gap: ${style.vars.space[3]};
  padding: ${style.vars.space[4]};
  padding-bottom: 0;
`;

const IconWrapper = styled.div`
  all: unset;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
`;

type ExpandableProps = {
  items: ItemizedSummaryLineItem[];
  totals: ItemizedSummaryTotals;
  UNSAFE_maxHeight?: number;
  UNSAFE_borderTop?: boolean;

  expandable: true;
  expanded: boolean;
  onToggleExpanded(): void;
};

type NonExpandableProps = {
  items: ItemizedSummaryLineItem[];
  totals: ItemizedSummaryTotals;
  UNSAFE_maxHeight?: number;
  UNSAFE_borderTop?: boolean;

  expandable?: false;
  expanded?: never;
};

type Props = ExpandableProps | NonExpandableProps;

export const ItemizedSummary: React.FC<Props> = (props) => {
  const { items, totals, UNSAFE_borderTop = false, UNSAFE_maxHeight } = props;

  const showItemizedList = !props.expandable || props.expanded;

  const onExpand = useEvent<React.MouseEventHandler<HTMLElement>>((ev) => {
    ev.preventDefault();

    if (props.expandable) {
      props.onToggleExpanded();
    }
  });

  return (
    <Container>
      {showItemizedList && items.length > 0 && (
        <>
          {UNSAFE_borderTop && <ItemizedSpacer />}
          <ItemizedList items={items} UNSAFE_maxHeight={UNSAFE_maxHeight} />
          <ItemizedSpacer />
        </>
      )}
      <GrandTotalWrapper role={props.expandable ? "button" : undefined} onClick={onExpand}>
        {props.expandable && (
          <IconWrapper>
            <Icon icon={props.expanded ? "arrow-down" : "arrow-up"} />
          </IconWrapper>
        )}
        <ItemizedGrandTotal centered={props.expandable ?? false} totals={totals} />
      </GrandTotalWrapper>
    </Container>
  );
};
